import React from 'react'
import { StatsCard } from './stats/StatsCard'
import { BuySellSplit } from './BuySellSplit'
import { Card } from '@/components/ui/card'
import TotalProjectsGraph from './TotalProjectsGraph'
import { AverageAmountCard } from './AverageAmountCard'
import { AssetsOnDiscountCard } from './AssetsOnDiscountCard'
import { TotalOrdersCard } from './TotalOrdersCard'
import { TotalValueCard } from './TotalValueCard'
import { formatCompactNumber } from '@/utils'
import { OrderbookStatsResponse } from '@/types/otc'
import { formatChartData } from './DashboardStats/DashboardStatsBase'
import inboxIcon from '@/assets/dashboard/inbox.svg'
import { useIsMobile } from '@/hooks/use-mobile'

interface OTCMetricsCardsProps {
  stats: OrderbookStatsResponse | null
  loading?: boolean
}

/**
 * Secondaries metrics cards (orderbook stats)
 * Displays 6 metric cards in 2 rows of 3 cards each as shown in the design
 */
export const OTCMetricsCards: React.FC<OTCMetricsCardsProps> = ({
  stats,
  loading = false
}) => {
  const isMobile = useIsMobile()

  // Calculate metrics
  const totalOrders = (stats?.buy_orders || 0) + (stats?.sell_orders || 0)
  const totalValue = stats?.total_orderbook_value || 0
  const buyOrders = stats?.buy_orders || 0
  const sellOrders = stats?.sell_orders || 0
  const averageAmount = totalOrders > 0 ? totalValue / totalOrders : 0
  const assetsOnDiscount = stats?.assets_on_discount || 0
  const totalProjects = stats?.total_projects || 0

  // Calculate buy/sell percentages
  const totalOrdersForSplit = buyOrders + sellOrders
  const buyPercentage = totalOrdersForSplit > 0 
    ? Math.round((buyOrders / totalOrdersForSplit) * 100) 
    : 0
  const sellPercentage = 100 - buyPercentage
  const totalProjectsChartData = formatChartData(stats?.total_projects_monthly_avg, 3)

  return (
    <div className="grid grid-cols-3 gap-[6px] md:grid-cols-2 lg:grid-cols-3 md:gap-4">
      {/* 1. Total Orders */}
      <TotalOrdersCard
        totalOrders={totalOrders}
        loading={loading}
        monthlyAvg={stats?.orderbook_monthly_avg}
        label={isMobile ? 'Total\nOrders' : 'Total Orders'}
      />

      {/* 2. Total Value */}
      <TotalValueCard
        totalValue={totalValue}
        loading={loading}
        monthlyAvg={stats?.orderbook_monthly_avg}
        label={isMobile ? 'Total\nValue' : 'Total Value'}
      />

      {/* 3. Buy/Sell Split */}
      <Card
        variant="default"
        padding="none"
        className="bg-white dark:bg-[#1c1c1c] rounded-none border border-white dark:border-[#1c1c1c] shadow-none relative h-[126px] p-4 md:h-[124px] md:px-6 md:py-6"
      >
        <BuySellSplit 
          buyCount={buyOrders}
          sellCount={sellOrders}
          buyPercentage={buyPercentage}
          sellPercentage={sellPercentage}
        />
      </Card>

      {/* 4. Average Amount */}
      <AverageAmountCard
        averageAmount={averageAmount}
        loading={loading}
        monthlyAvg={stats?.orderbook_monthly_avg}
        label={isMobile ? 'Average\nAmount' : 'Average Amount'}
      />

      {/* 5. Assets on Discount */}
      <AssetsOnDiscountCard
        assetsOnDiscount={assetsOnDiscount}
        loading={loading}
        monthlyAvg={stats?.orderbook_monthly_avg}
        label={isMobile ? 'Assets on\nDiscount' : 'Assets on Discount'}
      />

      {/* 6. Total Projects */}
      <StatsCard
        icon={inboxIcon}
        iconAlt="Total Projects"
        label={isMobile ? 'Total\nProjects' : 'Total Projects'}
        value={formatCompactNumber(totalProjects)}
        loading={loading}
        mobileHeight="h-[126px]"
        chart={
          <TotalProjectsGraph 
            data={totalProjectsChartData.length > 0 ? totalProjectsChartData : undefined}
          />
        }
      />
    </div>
  )
}
